import { Box, Tooltip, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  buildTimelineMonthEndMarkers,
  formatMonthLabel,
  getInteractionCountByMonth,
  getMonthlyScoreColor,
  getMonthlyScoreLabel,
  getSentimentColor,
  getTimelinePositionPercent,
  groupTimelineItemsByDate,
  TimelineItem,
  TimelineTooltipPayload,
} from "../../utils/utils";

type Props = {
  items: TimelineItem[];
  monthlyScore?: Record<string, string> | null;
  startDate: Date;
  endDate: Date;
  navigationState?: Record<string, any>;
  tooltip: {
    anchorEl: HTMLElement | null;
    activeItem: TimelineTooltipPayload | null;
    setAnchorEl: (el: HTMLElement | null) => void;
    setActiveItem: (item: TimelineTooltipPayload | null) => void;
  };
  onTimelineClick?: () => void;
};

const COLORS = {
  interaction: "#9478e4", // purple
  email: "#EFEBFB",       
};

const ACTIVE_COLORS = {
  interaction: "#6036d6",
  email: "#B6A3EC",
};

const HOVER_COLORS = {
  interaction: "#7C5AE0",
  email: "#CEC1F2",
};

const BORDERS = {
  interaction: "1px solid #EFEBFB",
  email: "1px solid #805EDE",
};
const DOT_SIZE = {
  interaction: 14,
  email: 8,
};

const CustomerTimelineCell = ({
  items = [],
  monthlyScore = {},
  startDate,
  endDate,
  navigationState,
  tooltip,
  onTimelineClick,
}: Props) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [hoveredTooltipId, setHoveredTooltipId] = useState<string | null>(null);
  const groupedItems = groupTimelineItemsByDate(items);
  const monthEndMarkers = buildTimelineMonthEndMarkers(
    monthlyScore,
    startDate,
    endDate
  );
  const interactionCountByMonth = useMemo(
    () => getInteractionCountByMonth(items),
    [items]
  );

  useEffect(() => {
    if (tooltip.anchorEl) {
      setHoveredTooltipId(null);
    }
  }, [tooltip.anchorEl]);

  const getDotStyles = (item: TimelineItem) => {
    const isHovered = hoveredId === item.id;
    const isActive = tooltip.activeItem?.interactions.some(
      (i) => i.id === item.id
    );

    const baseSize = DOT_SIZE[item.type];
    const activeSize = item.type === "interaction" ? baseSize + 4 : baseSize + 2;
    const currentMonthSentimentColor =
      item.type === "interaction"
        ? getSentimentColor(item.overallSentiment || null)
        : null;

    const backgroundColor = currentMonthSentimentColor
      ? currentMonthSentimentColor
      : isActive
      ? ACTIVE_COLORS[item.type]
      : isHovered
      ? HOVER_COLORS[item.type]
      : COLORS[item.type];

    const boxShadow =
      item.type === "interaction"
        ? (isActive || isHovered ? "0 0 0 1px white" : "none")
        : (isActive ? "0px 2px 4px 0px #00000040" : "none");

    return {
      position: "absolute",
      top: "80%",
      left: `${getTimelinePositionPercent(
        new Date(item.start_time),
        startDate,
        endDate
      )}%`,
      width: isHovered || isActive ? activeSize : baseSize,
      height: isHovered || isActive ? activeSize : baseSize,
      borderRadius: "50%",
      border: BORDERS[item.type],
      backgroundColor: backgroundColor,
      transform: "translate(-50%, -50%)",
      cursor: "pointer",
      boxShadow: boxShadow,
      transition: "all 0.18s ease-in-out",
      zIndex: isActive ? 3 : 2,
    };
  };

  const isActiveDetailDot = (items: TimelineItem[]) => {
    if (!tooltip.anchorEl || !tooltip.activeItem) return false;

    const activeIds = new Set(
      tooltip.activeItem.interactions.map((item) => item.id)
    );
    return (
      items.length === tooltip.activeItem.interactions.length &&
      items.every((item) => activeIds.has(item.id))
    );
  };

  if (!items.length && !monthEndMarkers.length) {
    return <Box sx={{ height: 32 }} />;
  }

  const handleDotClick = (
    e: React.MouseEvent<HTMLElement>,
    items: TimelineItem[],
    groupDateKey: string,
    anchorInteractionId: string
  ) => {
    // same tooltip already open for same interactions → ignore
    if (
      tooltip.anchorEl === e.currentTarget &&
      tooltip.activeItem &&
      tooltip.activeItem.interactions.length === items.length &&
      tooltip.activeItem.interactions.every(
        (i, idx) => i.id === items[idx].id
      )
    ) {
      return;
    }
    onTimelineClick?.();
    tooltip.setAnchorEl(e.currentTarget);
    const hasInteractions = items.some(
      i => i.type === "interaction"
    );

    const hasEmails = items.some(
      i => i.type === "email"
    );

    const isMixed = hasInteractions && hasEmails;

    tooltip.setActiveItem({
      mode: isMixed
        ? "mixed"
        : items.length > 1
        ? "group"
        : "single",

      interactions: items,
      emailIds: items
        .filter(i => i.type === "email")
        .map(i => i.id),
      navigationState,
      groupDateKey,
      anchorInteractionId,
    });
  };

  const getDotCountTooltip = (items: TimelineItem[]) => {
    const interactionCount = items.filter(
      (item) => item.type === "interaction"
    ).length;
    const emailCount = items.filter((item) => item.type === "email").length;
    const parts = [];

    if (interactionCount) {
      parts.push(
        `${interactionCount} Interaction${interactionCount > 1 ? "s" : ""}`
      );
    }

    if (emailCount) {
      parts.push(`${emailCount} Email${emailCount > 1 ? "s" : ""}`);
    }

    return parts.join(" & ");
  };

  return (
    <>
      <Box sx={{ position: "relative", height: 32, width: "100%", px: 1 }}>
        <Box
          sx={{
            position: "absolute",
            top: "80%",
            left: 0,
            right: 0,
            height: "1px",
            backgroundColor: "#805ede",
            transform: "translateY(-50%)",
          }}
        />

        {monthEndMarkers.map((marker) => {
          const interactionCount = interactionCountByMonth[marker.monthKey] || 0;
          const scoreLabel = getMonthlyScoreLabel(
            monthlyScore?.[marker.monthKey]
          );
          const scoreColor = getMonthlyScoreColor(scoreLabel);

          return (
            <Box
              key={marker.monthKey}
              sx={{
                position: "absolute",
                top: "70%",
                left:
                  marker.align === "right"
                    ? "auto"
                    : marker.align === "left"
                    ? 0
                    : `${marker.position}%`,
                right: marker.align === "right" ? -14 : "auto",
                transform:
                  marker.align === "left"
                    ? "translate(0, calc(-50% - 10px))"
                    : marker.align === "right"
                    ? "translate(0, calc(-50% - 10px))"
                    : "translate(-50%, calc(-50% - 10px))",
                zIndex: 2,
                pointerEvents: "auto",
                "&:hover": {
                  zIndex: 10,
                },
                "&:hover .monthly-trend-flag-icon": {
                  transform: "translateY(-2px) translateX(2px)",
                },
              }}
            >
              <Tooltip
                title={
                    <Box className="flags-tooltip-content">
                      <Box sx={{gap: "5px", display: "flex"}}>
                        <Typography className="flags-date-header">
                          {formatMonthLabel(marker.monthKey)} Trend:
                        </Typography>
                        <Typography>
                          <Box
                            component="span"
                            sx={{
                              color: scoreColor,
                              fontWeight: 600,
                            }}
                          >
                            {scoreLabel}
                          </Box>
                        </Typography>
                      </Box>
                      <Typography className="flags-interaction-count">
                        Interactions: {interactionCount}
                      </Typography>
                    </Box>
                }
                placement="bottom-start"
                classes={{ popper: "flags-tooltip" }}
              >
                <Box
                  className="monthly-trend-flag-icon"
                  component="span"
                  sx={{
                    display: "block",
                    transition: "transform 0.18s ease-in-out",
                  }}
                >
                  <img
                    src={marker.icon}
                    alt="monthly trend"
                    style={{ width: 26, height: 26, display: "block" }}
                  />
                </Box>
              </Tooltip>
            </Box>
          );
        })}

        {groupedItems.map((group) => {
          const hasInteractions = group.items.some(
            (i) => i.type === "interaction"
          );

          const hasEmails = group.items.some(
            (i) => i.type === "email"
          );

          const isMixed = hasInteractions && hasEmails;

          const interactionItem =
            group.items.find(i => i.type === "interaction");

          const emailItem =
            group.items.find(i => i.type === "email");

          const interactionStyles =
            interactionItem
              ? getDotStyles(interactionItem)
              : null;

          const emailStyles =
            emailItem
              ? getDotStyles(emailItem)
              : null;

          const representative =
            group.items.find((i) => i.type === "interaction") ??
            group.items[0];

          const dotProps = {
            "data-timeline-dot": "true",
            "data-interaction-id": representative.id,
            onMouseEnter: () => {
              setHoveredId(representative.id);
              setHoveredTooltipId(representative.id);
            },
            onMouseLeave: () => {
              setHoveredId(null);
              setHoveredTooltipId(null);
            },
            onClick: (e: React.MouseEvent<HTMLElement>) =>
              handleDotClick(
                e,
                group.items,
                group.dateKey,
                representative.id
              ),
          };
          const dotCountTooltip = getDotCountTooltip(group.items);
          const showHoverCountTooltip =
            hoveredTooltipId === representative.id &&
            !isActiveDetailDot(group.items);

          if (isMixed) {
            return (
              <Tooltip
                key={group.dateKey}
                title={dotCountTooltip}
                placement="top"
                arrow
                disableInteractive
                classes={{ popper: "tooltip-count" }}
                open={showHoverCountTooltip}
              >
                <Box
                  {...dotProps}
                  sx={{
                    position: "absolute",
                    top: interactionStyles?.top,
                    left: interactionStyles?.left,
                    transform: interactionStyles?.transform,
                    width: interactionStyles?.width,
                    height: interactionStyles?.height,
                    cursor: "pointer",
                    zIndex: interactionStyles?.zIndex,
                  }}
                >
                  <Box
                    sx={{
                      width: interactionStyles?.width,
                      height: interactionStyles?.height,
                      borderRadius: interactionStyles?.borderRadius,
                      border: interactionStyles?.border,
                      backgroundColor:
                        interactionStyles?.backgroundColor,
                      boxShadow: interactionStyles?.boxShadow,
                    }}
                  />

                  <Box
                    sx={{
                      position: "absolute",
                      width: emailStyles?.width,
                      height: emailStyles?.height,
                      borderRadius: emailStyles?.borderRadius,
                      border: emailStyles?.border,
                      backgroundColor:
                        emailStyles?.backgroundColor,
                      boxShadow: emailStyles?.boxShadow,
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                </Box>
              </Tooltip>
            );
          }

          return (
            <Tooltip
              key={group.dateKey}
              title={dotCountTooltip}
              placement="top"
              arrow
              disableInteractive
              classes={{ popper: "tooltip-count" }}
              open={showHoverCountTooltip}
            >
              <Box
                {...dotProps}
                sx={getDotStyles(representative)}
              />
            </Tooltip>
          );
        })}
      </Box>
    </>
  );
};

export default CustomerTimelineCell;
